import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { collection, query, where, onSnapshot, Timestamp } from 'firebase/firestore';
import { Plus, AlertTriangle, ChevronRight } from 'lucide-react';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';
import { useActiveTask } from '../../hooks/useActiveTask';
import { useTimer } from '../../hooks/useTimer';
import { formatElapsed, getDateRange } from '../../utils/time';
import { formatDuration, sanitizeProductionTask } from '../../utils/labour';
import { useCompanySettings } from '../../hooks/useCompanyData';
import { useAuth } from '../../context/AuthContext';
import './ProductionHome.css';

function ActiveTaskCard({ task, compact }) {
  const { t } = useTranslation();
  const { settings } = useCompanySettings();
  const navigate = useNavigate();
  const isPaused = !!task.pausedAt;
  const prevPausedSeconds = (task.pausedMinutes ?? 0) * 60;
  const timerElapsed = useTimer(
    task.startTime?.toDate?.() ?? task.startTime,
    !isPaused,
    prevPausedSeconds,
  );
  const frozenSeconds = useMemo(() => {
    if (!isPaused || !task.pausedAt || !task.startTime) return 0;
    const pausedAt = task.pausedAt.toDate ? task.pausedAt.toDate() : new Date(task.pausedAt);
    const startTime = task.startTime.toDate ? task.startTime.toDate() : new Date(task.startTime);
    return Math.max(0, Math.floor((pausedAt - startTime) / 1000) - prevPausedSeconds);
  }, [isPaused, task.pausedAt, task.startTime, prevPausedSeconds]);
  const elapsed = isPaused ? frozenSeconds : timerElapsed;
  const warnMinutes = settings?.activeTaskWarningMinutes ?? 240;
  const isLong = elapsed > warnMinutes * 60;

  const taskName = task.isCustomTaskName ? task.customTaskName : task.taskName;

  return (
    <button
      className={`home-active-card home-active-card-btn${compact ? ' home-active-card--compact' : ''}${isPaused ? ' is-paused' : isLong ? ' is-warning' : ''}`}
      onClick={() => navigate('/production/active-task')}
    >
      <div className="home-active-status">
        {!isPaused && <div className="live-dot" />}
        <span>{isPaused ? t('production.onBreak') : t('production.taskInProgress')}</span>
      </div>
      <div className="home-active-timer">{formatElapsed(elapsed)}</div>
      <div className="home-active-name">{taskName}</div>
      {task.roomName && <div className="home-active-room">{task.roomName}</div>}
      {!isPaused && isLong && (
        <div className="home-active-warning">
          <AlertTriangle size={14} />
          {t('production.taskWarning')}
        </div>
      )}
      <div className="home-active-tap-hint">
        <ChevronRight size={14} />
      </div>
    </button>
  );
}

function useTodayStats(user, companyId) {
  const [stats, setStats] = useState({ count: 0, minutes: 0 });

  useEffect(() => {
    if (!user || !companyId) return;
    const { start, end } = getDateRange('today');
    const q = query(
      collection(db, COLLECTIONS.PRODUCTION_TASKS),
      where('companyId', '==', companyId),
      where('createdBy', '==', user.uid),
      where('status', '==', 'completed'),
      where('archived', '==', false),
      where('submittedAt', '>=', Timestamp.fromDate(start)),
      where('submittedAt', '<=', Timestamp.fromDate(end))
    );
    return onSnapshot(q, (snap) => {
      const tasks = snap.docs.map((d) => sanitizeProductionTask(d.data()));
      setStats({
        count: tasks.length,
        minutes: tasks.reduce((s, t) => s + (t.durationMinutes ?? 0), 0),
      });
    }, (err) => {
      console.error('useTodayStats:', err);
    });
  }, [user, companyId]);

  return stats;
}

export default function ProductionHome() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeTasks, loading } = useActiveTask();
  const { user, profile, companyId } = useAuth();
  const todayStats = useTodayStats(user, companyId);

  if (loading) return <div className="page-loading"><div className="spinner spinner-lg" /></div>;

  const firstName = profile?.displayName?.split(' ')[0] ?? null;

  const statsRow = todayStats.count > 0 && (
    <div className="home-quick-stats">
      <div className="home-quick-stat">
        <span className="home-quick-stat-label">{t('production.todayTasks')}</span>
        <span className="home-quick-stat-value">{todayStats.count}</span>
      </div>
      <div className="home-quick-stat">
        <span className="home-quick-stat-label">{t('production.todayHours')}</span>
        <span className="home-quick-stat-value">{formatDuration(todayStats.minutes)}</span>
      </div>
    </div>
  );

  if (activeTasks.length > 0) {
    return (
      <div className="production-home">
        {firstName && (
          <p className="production-home-greeting">{t('production.greeting', { name: firstName })}</p>
        )}

        <div className="home-active">
          {activeTasks.map((task) => (
            <ActiveTaskCard key={task.id} task={task} compact={activeTasks.length > 1} />
          ))}

          <button
            className="btn btn-outline btn-lg btn-block"
            onClick={() => navigate('/production/start-task')}
          >
            <Plus size={20} />
            {t('production.startNewTask')}
          </button>
        </div>

        {statsRow}
      </div>
    );
  }

  return (
    <div className="production-home">
      <div className="production-home-hero">
        {firstName && (
          <p className="production-home-greeting">{t('production.greeting', { name: firstName })}</p>
        )}
        <p className="production-home-hint">{t('production.startTaskPrompt')}</p>
        <button
          className="btn btn-primary btn-start-task"
          onClick={() => navigate('/production/start-task')}
        >
          <Plus size={28} />
          {t('production.startNewTask')}
        </button>
      </div>

      {statsRow}
    </div>
  );
}
